<template>
  <div ref="containerRef" class="chain-container">
    <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
      <span class="fw-semibold me-2">Legend:</span>
      <span
        v-for="(color, type) in portfolioColorMap"
        :key="type"
        class="badge rounded-pill"
        :style="{ backgroundColor: color, color: '#fff' }"
      >
        {{ type }}
      </span>
    </div>

    <div class="tree-root d-flex flex-column align-items-center">
      <CertificateChainNode
        v-if="rootNode"
        :node="rootNode"
        :portfolioItems="portfolioItemsByCert"
        :portfolioColorMap="portfolioColorMap"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getChartColor } from "@/composables/chartPalette";
import CertificateChainNode from "./CertificateChainNode.vue";
import type {
  CertNode,
  PortfolioItem,
  TreeNode,
} from "./certificateChainTypes";

const props = defineProps<{
  certificates: CertNode[];
  portfolioItems: PortfolioItem[];
  rootCertificateId: number;
}>();

const certMap = computed(() => {
  const map = new Map<number, CertNode>();
  for (const c of props.certificates) {
    map.set(c.id, c);
  }
  return map;
});

const rootNode = computed<TreeNode | null>(() => {
  if (!props.certificates.length) return null;

  const childrenMap = new Map<number, CertNode[]>();
  for (const c of props.certificates) {
    if (c.previous_certificate_id !== null) {
      const siblings = childrenMap.get(c.previous_certificate_id) ?? [];
      siblings.push(c);
      childrenMap.set(c.previous_certificate_id, siblings);
    }
  }

  const buildTree = (cert: CertNode): TreeNode => ({
    cert,
    children: (childrenMap.get(cert.id) ?? [])
      .sort((a, b) => a.id - b.id)
      .map(buildTree),
  });

  const root = certMap.value.get(props.rootCertificateId);
  return root ? buildTree(root) : null;
});

const portfolioItemsByCert = computed(() => {
  const map = new Map<number, PortfolioItem[]>();
  for (const pi of props.portfolioItems) {
    const list = map.get(pi.certificate_id) ?? [];
    list.push(pi);
    map.set(pi.certificate_id, list);
  }
  return map;
});

const uniquePortfolioTypes = computed(() =>
  [...new Set(props.portfolioItems.map((pi) => pi.portfolio_type))].sort()
);

const portfolioColorMap = computed(() => {
  const types = uniquePortfolioTypes.value;
  const map: Record<string, string> = {};
  types.forEach((type, i) => {
    map[type] = getChartColor({ index: i });
  });
  return map;
});
</script>

<style scoped lang="scss">
.chain-container {
  min-height: 200px;
  overflow-x: auto;
}
</style>
